import { takeDataArray } from '../api/adapters'
import { apiFetch } from '../api/client'

export type PortalRequestType = 'viewing' | 'offer'

export interface PortalRequestMetadataRow {
    label: string
    value: string
}

export interface PortalBitrixRequest {
    orderId: number
    requestType: PortalRequestType
    productId: number
    createdAt: string
    statuses: string[]
    requesterUserId: number | null
    requesterCategory: string
    requesterName: string
    requesterEmail: string
    requesterPhone: string
    unitId: number | null
    unitLabel: string
    propertyLabel: string
    statusLabel: string
    preferredTime: string
    offerAmount: string
    comments: string
    metadataRows: PortalRequestMetadataRow[]
}

const VIEWING_REQUEST_PRODUCT_ID = 34
const OFFER_REQUEST_PRODUCT_ID = 35

const normalizeToken = (value: unknown): string =>
    String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '')

const toArray = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : [])

const toNumberOrNull = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value)
    return null
}

const stringifyValue = (value: unknown): string => {
    if (value == null) return ''
    if (typeof value === 'string') return value.trim()
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    if (Array.isArray(value)) return value.map((item) => stringifyValue(item)).filter(Boolean).join(', ')
    return ''
}

const flattenObject = (obj: Record<string, unknown>, prefix = ''): Array<{ key: string, value: unknown }> => {
    const rows: Array<{ key: string, value: unknown }> = []
    Object.entries(obj).forEach(([key, value]) => {
        const nextKey = prefix ? `${prefix}.${key}` : key
        if (value == null) return
        if (Array.isArray(value)) {
            rows.push({ key: nextKey, value })
            return
        }
        if (typeof value === 'object') {
            rows.push(...flattenObject(value as Record<string, unknown>, nextKey))
            return
        }
        rows.push({ key: nextKey, value })
    })
    return rows
}

const collectCandidateObjects = (order: any): Record<string, unknown>[] => {
    const service = toArray<any>(order?.Service)
    const attributes = order?.attributes
    const servicesMetadata = attributes?.ProductMetadata?.servicesMetadata
    const candidates: unknown[] = [
        order,
        attributes,
        attributes?.ProductMetadata,
        servicesMetadata,
        order?.Metadata,
        order?.MetaData,
        order?.RequestData,
        order?.Details,
        order?.Payload,
        service[0],
        service[0]?.Data,
        service[0]?.Details,
        service[0]?.Metadata,
        service[0]?.MetaData
    ]

    return candidates.filter((candidate) => candidate != null && typeof candidate === 'object') as Record<string, unknown>[]
}

const getValueByAliases = (objects: Record<string, unknown>[], aliases: string[]): unknown => {
    const aliasSet = new Set(aliases.map((alias) => normalizeToken(alias)))
    for (const objectItem of objects) {
        const rows = flattenObject(objectItem)
        for (const row of rows) {
            const leafKey = row.key.split('.').pop() ?? row.key
            if (aliasSet.has(normalizeToken(leafKey))) {
                return row.value
            }
        }
    }
    return undefined
}

const getOrderId = (order: any): number | null => toNumberOrNull(order?.id ?? order?.attributes?.id)

const getOrderCreatedAt = (order: any): string => {
    const raw = stringifyValue(order?.createdAt ?? order?.attributes?.createdAt ?? order?.InvoiceDate ?? order?.updatedAt)
    return raw || new Date().toISOString()
}

const getOrderStatuses = (order: any): string[] => {
    const statuses = toArray<string>(order?.Statuses ?? order?.Statuses ?? order?.attributes?.Statuses)
    if (statuses.length > 0) return statuses.map((status) => String(status))
    const singleStatus = stringifyValue(order?.Status ?? order?.attributes?.Status)
    return singleStatus ? [singleStatus] : ['In-process']
}

const getOrderProductId = (order: any): number | null => {
    const service = toArray<any>(order?.Service)
    const candidates: unknown[] = [
        order?.attributes?.ProductID,
        order?.attributes?.ProductId,
        order?.attributes?.productId,
        order?.ProductID,
        order?.ProductId,
        order?.productId,
        order?.Product?.id,
        order?.Product?.ID,
        service[0]?.ProductID,
        service[0]?.ProductId,
        service[0]?.productId,
        service[0]?.Product?.id,
        service[0]?.Product?.ID
    ]

    for (const candidate of candidates) {
        const parsed = toNumberOrNull(candidate)
        if (parsed != null) return parsed
    }
    return null
}

const getOrderRequesterUserId = (order: any, objects: Record<string, unknown>[]): number | null => {
    const directCandidates: unknown[] = [
        order?.attributes?.ProductMetadata?.servicesMetadata?.userId,
        order?.User?.id,
        order?.User?.data?.id,
        order?.attributes?.User?.id,
        order?.attributes?.User?.data?.id,
        order?.UserID,
        order?.UserId,
        order?.ClientID,
        order?.ClientId
    ]

    for (const candidate of directCandidates) {
        const parsed = toNumberOrNull(candidate)
        if (parsed != null) return parsed
    }

    const dynamic = getValueByAliases(objects, ['UserID', 'UserId', 'ClientID', 'ClientId', 'RequesterUserID'])
    return toNumberOrNull(dynamic)
}

const getOrderUnitId = (order: any, objects: Record<string, unknown>[]): number | null => {
    const directCandidates: unknown[] = [
        order?.attributes?.ProductMetadata?.servicesMetadata?.unitId,
        order?.attributes?.Unit?.data?.id,
        order?.Unit,
        order?.UnitID,
        order?.unitID,
        order?.Unit?.id,
        order?.Unit?.data?.id
    ]
    for (const candidate of directCandidates) {
        const parsed = toNumberOrNull(candidate)
        if (parsed != null) return parsed
    }
    const dynamic = getValueByAliases(objects, ['UnitID', 'UnitId', 'Unit', 'UnitReference', 'CatalogUnitID'])
    return toNumberOrNull(dynamic)
}

const getRequesterName = (user: any, objects: Record<string, unknown>[]): string => {
    const firstName = stringifyValue(user?.ContactInfo?.FirstName ?? user?.attributes?.ContactInfo?.FirstName)
    const familyName = stringifyValue(user?.ContactInfo?.FamilyName ?? user?.attributes?.ContactInfo?.FamilyName)
    const combined = `${firstName} ${familyName}`.trim()
    if (combined) return combined

    const metadataName = stringifyValue(getValueByAliases(objects, ['ClientName', 'RequesterName', 'Name', 'FullName']))
    if (metadataName) return metadataName
    return 'Unknown client'
}

const buildMetadataRows = (objects: Record<string, unknown>[]): PortalRequestMetadataRow[] => {
    const skipAliases = new Set([
        'productid',
        'productid',
        'statuses',
        'status',
        'unit',
        'unitid',
        'createdat',
        'updatedat',
        'name',
        'amount'
    ])

    const rows: PortalRequestMetadataRow[] = []
    const dedupe = new Set<string>()

    objects.forEach((objectItem) => {
        flattenObject(objectItem).forEach((row) => {
            const leafKey = row.key.split('.').pop() ?? row.key
            const normalized = normalizeToken(leafKey)
            if (skipAliases.has(normalized)) return
            const value = stringifyValue(row.value)
            if (!value) return
            const dedupeKey = `${normalized}:${value}`
            if (dedupe.has(dedupeKey)) return
            dedupe.add(dedupeKey)
            rows.push({
                label: leafKey,
                value
            })
        })
    })

    return rows.slice(0, 18)
}

const getOrderName = (order: any): string =>
    stringifyValue(order?.attributes?.Name ?? order?.Name)

const getOrderServicesMetadata = (order: any): Record<string, unknown> =>
    (order?.attributes?.ProductMetadata?.servicesMetadata ?? {}) as Record<string, unknown>

const isPreferredPortalChannel = (order: any): boolean => {
    const requestChannel = normalizeToken(getOrderServicesMetadata(order)?.requestChannel)
    return requestChannel === '' || requestChannel === 'bsocatalogueunitaction'
}

const isViewingRequestOrder = (order: any): boolean => {
    const servicesMetadata = getOrderServicesMetadata(order)
    const orderName = normalizeToken(getOrderName(order))
    const serviceId = normalizeToken(servicesMetadata?.ServiceId)
    const requestKind = normalizeToken(servicesMetadata?.requestKind)

    return isPreferredPortalChannel(order) && (
        orderName === normalizeToken('BSO Club Unit: Viewing Request')
        || serviceId === normalizeToken('service_bso_unit_viewing_request')
        || requestKind === 'viewing'
    )
}

const isOfferRequestOrder = (order: any): boolean => {
    const servicesMetadata = getOrderServicesMetadata(order)
    const orderName = normalizeToken(getOrderName(order))
    const serviceId = normalizeToken(servicesMetadata?.ServiceId)
    const requestKind = normalizeToken(servicesMetadata?.requestKind)

    return isPreferredPortalChannel(order) && (
        orderName === normalizeToken('BSO Club Unit: Offer Request')
        || serviceId === normalizeToken('service_bso_unit_offer_request')
        || requestKind === 'offer'
    )
}

export function mapPortalBitrixRequests(allUsers: any[], units: any[]): PortalBitrixRequest[] {
    const unitMap = new Map<number, any>()
    toArray<any>(units).forEach((unit) => {
        if (unit?.id != null) unitMap.set(Number(unit.id), unit)
    })

    const result: PortalBitrixRequest[] = []
    toArray<any>(allUsers).forEach((user) => {
        const orders = toArray<any>(user?.Orders)
        orders.forEach((order) => {
            const orderId = getOrderId(order)
            if (orderId == null) return
            const productId = getOrderProductId(order)
            if (productId !== VIEWING_REQUEST_PRODUCT_ID && productId !== OFFER_REQUEST_PRODUCT_ID) return

            const requestType: PortalRequestType = productId === VIEWING_REQUEST_PRODUCT_ID ? 'viewing' : 'offer'
            const objects = collectCandidateObjects(order)
            const unitId = getOrderUnitId(order, objects)
            const unit = unitId != null ? unitMap.get(unitId) : undefined

            const unitLabel = unit != null
                ? `Unit ${String(unit?.attributes?.Number ?? unit?.Number ?? unitId)}`
                : `Unit ${unitId != null ? String(unitId) : 'not linked'}`
            const propertyName = stringifyValue(unit?.attributes?.Property?.data?.attributes?.Name ?? unit?.Property?.Name)
            const propertyArea = stringifyValue(unit?.attributes?.Property?.data?.attributes?.Area ?? unit?.Property?.Area)
            const propertyLabel = [propertyName, propertyArea].filter(Boolean).join(', ') || 'Property not linked'

            const preferredTime = stringifyValue(getValueByAliases(objects, [
                'PreferredTime',
                'PreferredViewingTime',
                'ViewingTime',
                'PreferredDateTime',
                'Datetime',
                'AppointmentTime'
            ]))
            const offerAmount = stringifyValue(getValueByAliases(objects, [
                'OfferAmount',
                'Offer',
                'ProposedOffer',
                'OfferPrice',
                'RequestedOfferAmount'
            ]))
            const comments = stringifyValue(getValueByAliases(objects, [
                'Comment',
                'Comments',
                'Notes',
                'Note',
                'Message',
                'Description'
            ]))

            const statuses = getOrderStatuses(order)
            const requesterUserId = toNumberOrNull(user?.id)
            const requesterEmail = stringifyValue(user?.ContactInfo?.Email ?? user?.attributes?.ContactInfo?.Email ?? user?.email)
            const requesterPhone = stringifyValue(user?.ContactInfo?.Phone ?? user?.attributes?.ContactInfo?.Phone ?? user?.phone)

            result.push({
                orderId,
                requestType,
                productId,
                createdAt: getOrderCreatedAt(order),
                statuses,
                requesterUserId,
                requesterCategory: stringifyValue(user?.Category ?? user?.attributes?.Category),
                requesterName: getRequesterName(user, objects),
                requesterEmail: requesterEmail || 'Not set',
                requesterPhone: requesterPhone || 'Not set',
                unitId,
                unitLabel,
                propertyLabel,
                statusLabel: statuses[0] ?? 'In-process',
                preferredTime: preferredTime || 'Not provided',
                offerAmount: offerAmount || 'Not provided',
                comments: comments || 'Not provided',
                metadataRows: buildMetadataRows(objects)
            })
        })
    })

    return result.sort((a, b) => {
        const left = new Date(a.createdAt).getTime()
        const right = new Date(b.createdAt).getTime()
        return Number.isFinite(right - left) ? (right - left) : 0
    })
}

const getPortalOrdersQuery = (): Record<string, unknown> => ({
    filters: {
        $or: [
            {
                ProductID: {
                    $eq: VIEWING_REQUEST_PRODUCT_ID
                }
            },
            {
                ProductID: {
                    $eq: OFFER_REQUEST_PRODUCT_ID
                }
            }
        ]
    },
    populate: {
        Service: '*',
        Unit: {
            fields: ['id']
        },
        User: {
            fields: ['id']
        }
    },
    sort: ['createdAt:desc'],
    pagination: {
        page: 1,
        pageSize: 5000
    },
    noLimit: true
})

export async function fetchPortalOrdersFromApiV2(): Promise<any[]> {
    const result = await apiFetch<any[]>({
        v2: {
            path: '/orders',
            params: getPortalOrdersQuery()
        },
        adapter: takeDataArray,
        timeoutMs: 20000
    })

    if (!result.ok) {
        throw new Error(result.error)
    }

    return result.data
}

export function mapPortalBitrixRequestsFromOrders(orders: any[], allUsers: any[], units: any[]): PortalBitrixRequest[] {
    const unitMap = new Map<number, any>()
    toArray<any>(units).forEach((unit) => {
        if (unit?.id != null) unitMap.set(Number(unit.id), unit)
    })

    const usersMap = new Map<number, any>()
    toArray<any>(allUsers).forEach((user) => {
        if (user?.id != null) usersMap.set(Number(user.id), user)
    })

    const result: PortalBitrixRequest[] = []
    toArray<any>(orders).forEach((order) => {
        const orderId = getOrderId(order)
        if (orderId == null) return

        let requestType: PortalRequestType | null = null
        if (isViewingRequestOrder(order)) requestType = 'viewing'
        else if (isOfferRequestOrder(order)) requestType = 'offer'
        if (requestType == null) return

        const productId = getOrderProductId(order) ?? (requestType === 'viewing' ? VIEWING_REQUEST_PRODUCT_ID : OFFER_REQUEST_PRODUCT_ID)
        const objects = collectCandidateObjects(order)
        const unitId = getOrderUnitId(order, objects)
        const unit = unitId != null ? unitMap.get(unitId) : undefined
        const requesterUserId = getOrderRequesterUserId(order, objects)
        const requesterUser = requesterUserId != null ? usersMap.get(requesterUserId) : null

        const unitLabel = unit != null
            ? `Unit ${String(unit?.attributes?.Number ?? unit?.Number ?? unitId)}`
            : `Unit ${unitId != null ? String(unitId) : 'not linked'}`
        const propertyName = stringifyValue(unit?.attributes?.Property?.data?.attributes?.Name ?? unit?.Property?.Name)
        const propertyArea = stringifyValue(unit?.attributes?.Property?.data?.attributes?.Area ?? unit?.Property?.Area)
        const propertyLabel = [propertyName, propertyArea].filter(Boolean).join(', ') || 'Property not linked'

        const preferredTime = stringifyValue(getValueByAliases(objects, [
            'PreferredTime',
            'PreferredViewingTime',
            'ViewingTime',
            'PreferredDateTime',
            'Datetime',
            'AppointmentTime'
        ]))
        const offerAmount = stringifyValue(getValueByAliases(objects, [
            'OfferAmount',
            'Offer',
            'ProposedOffer',
            'OfferPrice',
            'RequestedOfferAmount'
        ]))
        const comments = stringifyValue(getValueByAliases(objects, [
            'Comment',
            'Comments',
            'Notes',
            'Note',
            'Message',
            'Description'
        ]))

        const statuses = getOrderStatuses(order)
        const requesterEmail = stringifyValue(requesterUser?.ContactInfo?.Email ?? requesterUser?.attributes?.ContactInfo?.Email ?? requesterUser?.email)
        const requesterPhone = stringifyValue(requesterUser?.ContactInfo?.Phone ?? requesterUser?.attributes?.ContactInfo?.Phone ?? requesterUser?.phone)

        result.push({
            orderId,
            requestType,
            productId,
            createdAt: getOrderCreatedAt(order),
            statuses,
            requesterUserId,
            requesterCategory: stringifyValue(requesterUser?.Category ?? requesterUser?.attributes?.Category),
            requesterName: getRequesterName(requesterUser, objects),
            requesterEmail: requesterEmail || 'Not set',
            requesterPhone: requesterPhone || 'Not set',
            unitId,
            unitLabel,
            propertyLabel,
            statusLabel: statuses[0] ?? 'In-process',
            preferredTime: preferredTime || 'Not provided',
            offerAmount: offerAmount || 'Not provided',
            comments: comments || 'Not provided',
            metadataRows: buildMetadataRows(objects)
        })
    })

    return result.sort((a, b) => {
        const left = new Date(a.createdAt).getTime()
        const right = new Date(b.createdAt).getTime()
        return Number.isFinite(right - left) ? (right - left) : 0
    })
}
